/**
 * « MES DOCUMENTS » — module PUR, partagé par « Ma vue » (tableau de bord) et
 * le filtre de l'écran Documents (RQ, 04/10/2026).
 *
 * Deux questions distinctes, et il ne faut pas les confondre :
 *
 *  · À MOI D'AGIR — c'est MON TOUR : rédiger un brouillon dont je suis
 *    rédacteur, vérifier ou approuver ce qui m'est désigné (règle du serveur,
 *    `taches-signature.ts`, y compris ce qui m'est désigné mais BLOQUÉ par la
 *    séparation des tâches : caché, il resterait en revue sans que personne ne
 *    le sache), lire ce qui m'a été diffusé sans accusé ;
 *  · M'IMPLIQUANT — j'ai un rôle sur ce document (rédacteur, vérificateur,
 *    approbateur, destinataire), que ce soit mon tour ou non.
 *
 * Le tableau de bord et le filtre lisent CES fonctions : « Voir les 23 » doit
 * ouvrir exactement les 23 qu'on vient de compter.
 */
import type { GedDocument } from './types';
import { attendDeMoi, signatureBloquee } from './taches-signature';

export type EtapeMoi = 'rediger' | 'verifier' | 'approuver' | 'lire';
type Personne = { key: string; roles: string[] };

export const ETAPES_MOI: { code: EtapeMoi; label: string }[] = [
  { code: 'rediger', label: 'rédiger' },
  { code: 'verifier', label: 'vérifier' },
  { code: 'approuver', label: 'approuver' },
  { code: 'lire', label: 'lire (accusé attendu)' },
];

export function estRedacteur(d: GedDocument, cle: string): boolean {
  return d.redacteurs?.length ? d.redacteurs.includes(cle) : d.redacteur === cle;
}

/** Les étapes qui m'attendent sur ce document — vide si ce n'est pas mon tour. */
export function etapesAMoi(d: GedDocument, moi: Personne): EtapeMoi[] {
  if (!moi.key) return [];
  const out: EtapeMoi[] = [];
  if (['brouillon', 'enrevision'].includes(d.statut) && estRedacteur(d, moi.key)) out.push('rediger');
  const sig = attendDeMoi(d, moi) || signatureBloquee(d, moi);
  if (sig === 'verification') out.push('verifier');
  if (sig === 'approbation') out.push('approuver');
  if ((d.diffusion || []).some((x) => x.userId === moi.key && x.requis && !x.dateAck)) out.push('lire');
  return out;
}

/** J'ai un rôle sur ce document, quel que soit le tour. */
export function mImplique(d: GedDocument, moi: Personne): boolean {
  if (!moi.key) return false;
  return estRedacteur(d, moi.key)
    || d.verificateur === moi.key
    || d.approbateur === moi.key
    || (d.diffusion || []).some((x) => x.userId === moi.key);
}

/** Le filtre « Mes documents » de l'écran Documents. Valeurs :
 *  '' (tous) · 'amoi' · 'amoi:<etape>' · 'implique'. */
export function filtreMoi(d: GedDocument, moi: Personne, valeur: string): boolean {
  if (!valeur) return true;
  if (valeur === 'implique') return mImplique(d, moi);
  const etapes = etapesAMoi(d, moi);
  if (valeur === 'amoi') return etapes.length > 0;
  const [, etape] = valeur.split(':');
  return etapes.includes(etape as EtapeMoi);
}
